import "./ClaimTypeFilters.css";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FormControl, Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info"; 
import {
  setSelectedClaimType,
  setSelectedClaimSubType,
  setMonth,
} from "../../../stateManagement/actions/claims";
import {getLast12Months} from "../../../utils/calenderUtils";
import { useEffect } from "react";
import CustomSelect from "../../../components/dms_common/CustomSelect";
import { logAnalyticsEvent } from "../../../../src/firebase";

const ClaimTypeFilters = () => {
  const { selectedClaimType, selectedClaimSubType, month } = useSelector(
    (store) => store.claims
  );
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const { claimTypesObj } = ConfigDictionary;
  const distributorId = JSON.parse(localStorage.authContext).user.loginId;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const orderedClaimTypes = [
    claimTypesObj?.DAMAGERETURNS,
    claimTypesObj?.SECONDARYSCHEMES,
    claimTypesObj?.OTHERS
  ].filter(Boolean);  

  const claimTypes = orderedClaimTypes.map(type => ({
    id: type.id,
    label: type.label,
    selectLabel: type.selectLabel
  }));

  const allSubTypes = {};
  const tooltipDescriptions = {};
  const path = {};
  const requiresMonth = {};

  orderedClaimTypes.forEach(claimType => {
    Object.values(claimType.subTypes).forEach(subType => {
      allSubTypes[subType.label] = {
        ...subType,
        claimTypeLabel: claimType.label
      };
      tooltipDescriptions[subType.label] = subType.description;
      path[subType.label] = subType.path;
      requiresMonth[subType.label] = subType.requiresMonth;
    });
  });

  const claimSubTypeOptions = {};
  orderedClaimTypes.forEach(claimType => {
    claimSubTypeOptions[claimType.label] = {
      options: Object.values(claimType.subTypes).map(subType => subType.label),
      selectLabel: claimType.selectLabel
    };
  });

  useEffect(() => {
    if (!month) {
      const currentMonth = getLast12Months()[0];
      dispatch(setMonth(currentMonth));
    }
  }, [month, dispatch]);

  const handleClaimTypeChange = (value) => {
    dispatch(setSelectedClaimType(value));
    dispatch(setSelectedClaimSubType(""));
    logAnalyticsEvent("claim_type_selected", {
      distributor_id: distributorId,
      claim_type: value
    });
    if (path[value]) navigate(`/dashboard/claims/create/${path[value]}`);
    else navigate("/dashboard/claims/create");
  };

  const handleClaimSubTypeChange = (value) => {
    dispatch(setSelectedClaimSubType(value));
    logAnalyticsEvent("claim_subtype_selected", {
      distributor_id: distributorId,
      claim_type: selectedClaimType,
      claim_subtype: value
    });
    navigate(path[value]);
  };

  const handleMonthChange = (value) => {
    dispatch(setMonth(value));
  };

  const getSelectLabel = () => {
    if (!selectedClaimType) return "";
    const claimType = claimTypes.find(type => type.label === selectedClaimType);
    return claimType?.selectLabel || "";
  };

  return (
    <div className="claim-type-filters-root">
      <div className="type-select">
        <div className="claim-type-select">
          <p className="claim-type-select-label">
            Select Claim Type<span>*</span>&nbsp;&nbsp;&nbsp;
          </p>
          <FormControl className="manage_order_filter">
            <CustomSelect
              options={claimTypes.map(option => ({
                label: option.label,
                value: option.label
              }))}
              value={selectedClaimType}
              onChange={(e) => handleClaimTypeChange(e.target.value)}
              placeholder="Select Here"
            />
          </FormControl>
        </div>
        {selectedClaimType && (
          <div className="damage-type-select">
            <p className="claim-type-select-label">
              {getSelectLabel()}
              <span>*</span>&nbsp;&nbsp;&nbsp;
            </p>
            <FormControl className="manage_order_filter">
              <CustomSelect
                options={(
                  claimSubTypeOptions[selectedClaimType]?.options || []
                ).map((subOption) => ({
                  label: (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {subOption}
                      <Tooltip
                        title={tooltipDescriptions[subOption]}
                        placement="right"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: "#FFFFFF !important",
                              color: "#000000 !important",
                              boxShadow:
                                "0px 4px 20px rgba(0, 0, 0, 0.1) !important",
                              fontWeight: 400,
                            },
                          },
                          arrow: {
                            sx: {
                              color: "#FFFFFF !important",
                            },
                          },
                        }}
                      >
                        <InfoIcon
                          style={{
                            fontSize: "16px",
                            marginLeft: "8px",
                            color: "#F29C38",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </div>
                  ),
                  value: subOption,
                }))}
                value={selectedClaimSubType}
                onChange={(e) => handleClaimSubTypeChange(e.target.value)}
                placeholder="Select Here"
                renderValue={(selected) => (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {selected}
                    <Tooltip
                      title={tooltipDescriptions[selected]}
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: "#FFFFFF !important",
                            color: "#000000 !important",
                            boxShadow:
                              "0px 4px 20px rgba(0, 0, 0, 0.1) !important",
                            fontWeight: 400,
                          },
                        },
                        arrow: {
                          sx: {
                            color: "#FFFFFF !important",
                          },
                        },
                      }}
                    >
                      <InfoIcon
                        style={{
                          fontSize: "16px",
                          marginLeft: "8px",
                          color: "#F29C38",
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  </div>
                )}
              />
            </FormControl>
          </div>
        )}
      </div>
      <div className="date-select">
        {selectedClaimSubType && requiresMonth[selectedClaimSubType] && (
          <FormControl className="manage_order_filter">
            <CustomSelect
              options={getLast12Months().map((subOption) => ({
                label: subOption,
                value: subOption,
              }))}
              value={month || getLast12Months()?.[0]}
              onChange={(e) => handleMonthChange(e.target.value)}
              placeholder="Select Month"
            />
          </FormControl>
        )}
      </div>
    </div>
  );
};

export default ClaimTypeFilters;